import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Home, Cat, CheckSquare, CalendarDays, Package,
  Plus, Trash2, Edit, Check, Circle, AlertTriangle,
  Phone, Heart, RefreshCw, Star, X, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FRECUENCIAS = [
  { value: 'diaria', label: 'Diaria' },
  { value: 'semanal', label: 'Semanal' },
  { value: '2_semana', label: '2 veces/semana' },
  { value: '3_semana', label: '3 veces/semana' },
  { value: 'mensual', label: 'Mensual' },
];

const TIPO_EVENTO = {
  gato: { label: 'Gato', color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', icon: Cat },
  casa: { label: 'Casa', color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30', icon: Home },
  humano: { label: 'Humano', color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', icon: Heart },
};

export default function ControlFamiliar() {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [gato, setGato] = useState({});
  const [tareas, setTareas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [miembros, setMiembros] = useState([]);
  const [loading, setLoading] = useState(true);

  // Forms
  const [showGatoForm, setShowGatoForm] = useState(false);
  const [gatoForm, setGatoForm] = useState({});
  const [showTareaForm, setShowTareaForm] = useState(false);
  const [tareaForm, setTareaForm] = useState({ nombre: '', frecuencia: 'diaria', asignado_a: '', asignado_nombre: '' });
  const [editTarea, setEditTarea] = useState(null);
  const [showEventoForm, setShowEventoForm] = useState(false);
  const [eventoForm, setEventoForm] = useState({ titulo: '', fecha: '', tipo: 'casa', notificacion_dias: 1 });
  const [editEvento, setEditEvento] = useState(null);
  const [showInvForm, setShowInvForm] = useState(false);
  const [invForm, setInvForm] = useState({ tipo: 'comida', nombre: '', cantidad: 0, unidad: 'kg', umbral: 1 });
  const [editInv, setEditInv] = useState(null);
  const [showReasignar, setShowReasignar] = useState(null);

  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadAll = useCallback(async () => {
    try {
      const [dashRes, miembrosRes] = await Promise.all([
        axios.get(`${API}/familia/dashboard`, { headers }),
        axios.get(`${API}/familia/miembros`, { headers })
      ]);
      setDashboard(dashRes.data);
      setMiembros(miembrosRes.data);
      setGato(dashRes.data.gato || {});
      setTareas(dashRes.data.tareas_hoy || []);
      setEventos(dashRes.data.proximos_eventos || []);
      setInventario(dashRes.data.inventario || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadTareas = async () => {
    const { data } = await axios.get(`${API}/familia/tareas`, { headers });
    setTareas(data);
  };
  const loadEventos = async () => {
    const { data } = await axios.get(`${API}/familia/eventos`, { headers });
    setEventos(data);
  };
  const loadInventario = async () => {
    const { data } = await axios.get(`${API}/familia/inventario`, { headers });
    setInventario(data);
  };
  const loadGato = async () => {
    const { data } = await axios.get(`${API}/familia/gato`, { headers });
    setGato(data);
  };

  useEffect(() => { loadAll(); }, [loadAll]);

  // --- Handlers ---
  const saveGato = async () => {
    await axios.post(`${API}/familia/gato`, gatoForm, { headers });
    setShowGatoForm(false);
    loadGato();
  };

  const saveTarea = async () => {
    if (!tareaForm.nombre.trim()) return;
    if (editTarea) {
      await axios.put(`${API}/familia/tareas/${editTarea}`, tareaForm, { headers });
    } else {
      await axios.post(`${API}/familia/tareas`, tareaForm, { headers });
    }
    setShowTareaForm(false);
    setEditTarea(null);
    setTareaForm({ nombre: '', frecuencia: 'diaria', asignado_a: '', asignado_nombre: '' });
    loadTareas();
  };

  const deleteTarea = async (id) => {
    if (!window.confirm('Eliminar tarea?')) return;
    await axios.delete(`${API}/familia/tareas/${id}`, { headers });
    loadTareas();
  };

  const toggleTarea = async (id) => {
    await axios.post(`${API}/familia/tareas/${id}/completar`, {}, { headers });
    loadTareas();
  };

  const reasignarTarea = async (tareaId, miembro) => {
    await axios.put(`${API}/familia/tareas/${tareaId}`, {
      asignado_a: miembro.user_id,
      asignado_nombre: miembro.nombre
    }, { headers });
    setShowReasignar(null);
    loadTareas();
  };

  const saveEvento = async () => {
    if (!eventoForm.titulo.trim() || !eventoForm.fecha) return;
    if (editEvento) {
      await axios.put(`${API}/familia/eventos/${editEvento}`, eventoForm, { headers });
    } else {
      await axios.post(`${API}/familia/eventos`, eventoForm, { headers });
    }
    setShowEventoForm(false);
    setEditEvento(null);
    setEventoForm({ titulo: '', fecha: '', tipo: 'casa', notificacion_dias: 1 });
    loadEventos();
  };

  const deleteEvento = async (id) => {
    if (!window.confirm('Eliminar evento?')) return;
    await axios.delete(`${API}/familia/eventos/${id}`, { headers });
    loadEventos();
  };

  const saveInv = async () => {
    if (!invForm.nombre.trim()) return;
    if (editInv) {
      await axios.put(`${API}/familia/inventario/${editInv}`, invForm, { headers });
    } else {
      await axios.post(`${API}/familia/inventario`, invForm, { headers });
    }
    setShowInvForm(false);
    setEditInv(null);
    setInvForm({ tipo: 'comida', nombre: '', cantidad: 0, unidad: 'kg', umbral: 1 });
    loadInventario();
  };

  const deleteInv = async (id) => {
    if (!window.confirm('Eliminar item?')) return;
    await axios.delete(`${API}/familia/inventario/${id}`, { headers });
    loadInventario();
  };

  const updateInvCantidad = async (id, cantidad) => {
    await axios.put(`${API}/familia/inventario/${id}`, { cantidad: parseFloat(cantidad) }, { headers });
    loadInventario();
  };

  const hoy = new Date().toISOString().slice(0, 10);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-dark-mk flex items-center justify-center">
          <div className="text-gray-500">Cargando...</div>
        </div>
      </>
    );
  }

  // --- TABS ---
  const tabs = [
    { id: 'dashboard', label: 'Inicio', icon: Home },
    { id: 'gato', label: 'Gato', icon: Cat },
    { id: 'tareas', label: 'Tareas', icon: CheckSquare },
    { id: 'calendario', label: 'Eventos', icon: CalendarDays },
    { id: 'inventario', label: 'Stock', icon: Package },
  ];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-dark-mk" data-testid="control-familiar">

        {/* Header */}
        <div className="px-4 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/30 to-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <Home className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white leading-tight">Control Familiar</h1>
              <p className="text-gray-500 text-xs">Familia Montero</p>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="px-4 mb-4">
          <div className="flex bg-gray-900/80 rounded-lg p-0.5 border border-gray-800" data-testid="familia-tabs">
            {tabs.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-md text-[10px] font-medium transition-all ${
                    tab === t.id ? 'bg-amber-500/20 text-amber-400' : 'text-gray-500 hover:text-gray-300'
                  }`} data-testid={`tab-${t.id}`}>
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ═══ DASHBOARD TAB ═══ */}
        {tab === 'dashboard' && dashboard && (
          <div className="px-4 pb-24 space-y-4" data-testid="dashboard-tab">

            {/* Gato summary */}
            {gato?.nombre && (
              <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4" data-testid="dash-gato">
                <div className="flex items-center gap-2 mb-3">
                  <Cat className="h-4 w-4 text-amber-400" />
                  <span className="text-amber-400 text-xs font-bold uppercase tracking-wider">{gato.nombre}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {inventario.map(inv => {
                    const isLow = inv.cantidad <= inv.umbral;
                    return (
                      <div key={inv.id} className={`rounded-lg p-2.5 border ${isLow ? 'bg-red-900/20 border-red-500/30' : 'bg-gray-800/60 border-gray-700/60'}`}>
                        <p className="text-gray-400 text-[10px] uppercase font-bold">{inv.nombre}</p>
                        <p className={`text-lg font-black tabular-nums ${isLow ? 'text-red-400' : 'text-white'}`}>
                          {inv.cantidad} <span className="text-xs font-normal text-gray-500">{inv.unidad}</span>
                        </p>
                        {isLow && <p className="text-red-400 text-[9px] font-bold mt-0.5">REPONER</p>}
                      </div>
                    );
                  })}
                </div>
                {dashboard.proximo_evento_gato && (
                  <div className="mt-2 flex items-center gap-2 bg-amber-500/10 rounded-lg px-3 py-2 border border-amber-500/20">
                    <Star className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
                    <span className="text-amber-400 text-xs font-medium">{dashboard.proximo_evento_gato.titulo}</span>
                    <span className="text-gray-500 text-xs ml-auto">{dashboard.proximo_evento_gato.fecha}</span>
                  </div>
                )}
              </div>
            )}

            {/* Tareas hoy */}
            <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4" data-testid="dash-tareas">
              <div className="flex items-center gap-2 mb-3">
                <CheckSquare className="h-4 w-4 text-green-400" />
                <span className="text-green-400 text-xs font-bold uppercase tracking-wider">Tareas hoy</span>
                <span className="ml-auto text-gray-600 text-xs">{tareas.filter(t => t.completado_hoy || t.ultimo_completado === hoy).length}/{tareas.length}</span>
              </div>
              {tareas.length === 0 ? (
                <p className="text-gray-600 text-sm">No hay tareas creadas</p>
              ) : (
                <div className="space-y-2">
                  {tareas.map(t => {
                    const done = t.completado_hoy || t.ultimo_completado === hoy;
                    return (
                      <button key={t.id} onClick={() => toggleTarea(t.id)}
                        className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-all text-left ${
                          done ? 'bg-green-900/20 border border-green-500/30' : 'bg-gray-800/60 border border-gray-700/60 hover:border-gray-600'
                        }`} data-testid={`dash-tarea-${t.id}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          done ? 'bg-green-500 text-white' : 'bg-gray-700 border border-gray-600'
                        }`}>
                          {done ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3 text-gray-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${done ? 'text-green-300/80 line-through' : 'text-white'}`}>{t.nombre}</p>
                          <p className="text-gray-500 text-[10px]">{t.asignado_nombre || 'Sin asignar'}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Proximos eventos */}
            <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-4" data-testid="dash-eventos">
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="h-4 w-4 text-blue-400" />
                <span className="text-blue-400 text-xs font-bold uppercase tracking-wider">Proximos eventos</span>
              </div>
              {eventos.length === 0 ? (
                <p className="text-gray-600 text-sm">No hay eventos proximos</p>
              ) : (
                <div className="space-y-2">
                  {eventos.slice(0, 5).map(ev => {
                    const tc = TIPO_EVENTO[ev.tipo] || TIPO_EVENTO.casa;
                    const Icon = tc.icon;
                    const daysUntil = Math.ceil((new Date(ev.fecha + 'T00:00:00') - new Date(hoy + 'T00:00:00')) / (1000*60*60*24));
                    return (
                      <div key={ev.id} className={`flex items-center gap-3 p-2.5 rounded-lg ${tc.bg} border ${tc.border}`}>
                        <Icon className={`h-4 w-4 ${tc.color} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{ev.titulo}</p>
                          <p className="text-gray-500 text-[10px]">{ev.fecha}</p>
                        </div>
                        <span className={`text-xs font-bold tabular-nums ${daysUntil <= 3 ? 'text-red-400' : tc.color}`}>
                          {daysUntil === 0 ? 'HOY' : daysUntil === 1 ? 'Manana' : `${daysUntil}d`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ GATO TAB ═══ */}
        {tab === 'gato' && (
          <div className="px-4 pb-24" data-testid="gato-tab">
            {!gato?.nombre ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                <Cat className="h-16 w-16 mb-3 opacity-30" />
                <p className="text-sm mb-3">Aun no hay perfil del gato</p>
                <Button onClick={() => { setGatoForm({}); setShowGatoForm(true); }}
                  className="bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30"
                  data-testid="btn-add-gato">
                  <Plus className="h-4 w-4 mr-1" /> Crear perfil
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Cat card */}
                <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/30 to-amber-700/20 border border-amber-500/30 flex items-center justify-center">
                        <Cat className="h-7 w-7 text-amber-400" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-black text-white">{gato.nombre}</h2>
                        {gato.fecha_nacimiento && (
                          <p className="text-gray-500 text-xs">Nacido: {gato.fecha_nacimiento}</p>
                        )}
                      </div>
                    </div>
                    <button onClick={() => { setGatoForm({...gato}); setShowGatoForm(true); }}
                      className="text-gray-600 hover:text-amber-400 p-2" data-testid="btn-edit-gato">
                      <Edit className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    {gato.veterinario_nombre && (
                      <div className="flex items-center gap-3 bg-gray-800/60 rounded-lg p-3 border border-gray-700/60">
                        <Heart className="h-4 w-4 text-red-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium">{gato.veterinario_nombre}</p>
                          <p className="text-gray-500 text-[10px] uppercase">Veterinario</p>
                        </div>
                        {gato.veterinario_telefono && (
                          <a href={`tel:${gato.veterinario_telefono}`}
                            className="bg-green-500/20 text-green-400 p-2 rounded-lg border border-green-500/30">
                            <Phone className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    )}
                    {gato.microchip && (
                      <div className="flex items-center gap-3 bg-gray-800/60 rounded-lg p-3 border border-gray-700/60">
                        <Star className="h-4 w-4 text-cyan-400 flex-shrink-0" />
                        <div>
                          <p className="text-white text-sm font-mono">{gato.microchip}</p>
                          <p className="text-gray-500 text-[10px] uppercase">Microchip</p>
                        </div>
                      </div>
                    )}
                    {gato.observaciones && (
                      <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700/60">
                        <p className="text-gray-400 text-[10px] uppercase font-bold mb-1">Observaciones</p>
                        <p className="text-white text-sm">{gato.observaciones}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ TAREAS TAB ═══ */}
        {tab === 'tareas' && (
          <div className="px-4 pb-24" data-testid="tareas-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">{tareas.length} tareas</p>
              <Button size="sm" onClick={() => { setTareaForm({ nombre: '', frecuencia: 'diaria', asignado_a: '', asignado_nombre: '' }); setEditTarea(null); setShowTareaForm(true); }}
                className="bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 h-8 text-xs"
                data-testid="btn-add-tarea">
                <Plus className="h-3.5 w-3.5 mr-1" /> Nueva
              </Button>
            </div>
            <div className="space-y-2">
              {tareas.map(t => {
                const done = t.completado_hoy || t.ultimo_completado === hoy;
                return (
                  <div key={t.id} className={`rounded-xl p-3 border transition-all ${
                    done ? 'bg-green-900/20 border-green-500/30' : 'bg-gray-900/80 border-gray-800'
                  }`} data-testid={`tarea-${t.id}`}>
                    <div className="flex items-center gap-3">
                      <button onClick={() => toggleTarea(t.id)}
                        className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                          done ? 'bg-green-500 text-white' : 'bg-gray-800 border border-gray-600 hover:border-gray-400'
                        }`} data-testid={`check-tarea-${t.id}`}>
                        {done ? <Check className="h-4 w-4" /> : <Circle className="h-3 w-3 text-gray-600" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${done ? 'text-green-300/80 line-through' : 'text-white'}`}>{t.nombre}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-gray-500 text-[10px]">{FRECUENCIAS.find(f => f.value === t.frecuencia)?.label || t.frecuencia}</span>
                          <span className="text-gray-700 text-[10px]">|</span>
                          <span className="text-amber-400 text-[10px] font-medium">{t.asignado_nombre || 'Sin asignar'}</span>
                        </div>
                      </div>
                      <button onClick={() => setShowReasignar(showReasignar === t.id ? null : t.id)}
                        className="text-gray-600 hover:text-amber-400 p-1.5" title="Reasignar" data-testid={`btn-reasignar-${t.id}`}>
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => { setTareaForm({ nombre: t.nombre, frecuencia: t.frecuencia, asignado_a: t.asignado_a, asignado_nombre: t.asignado_nombre }); setEditTarea(t.id); setShowTareaForm(true); }}
                        className="text-gray-600 hover:text-white p-1.5" data-testid={`btn-edit-tarea-${t.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => deleteTarea(t.id)}
                        className="text-gray-600 hover:text-red-400 p-1.5" data-testid={`btn-del-tarea-${t.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {/* Reasignar dropdown */}
                    {showReasignar === t.id && (
                      <div className="mt-2 ml-10 flex gap-2" data-testid={`reasignar-${t.id}`}>
                        {miembros.map(m => (
                          <button key={m.user_id} onClick={() => reasignarTarea(t.id, m)}
                            className={`text-xs px-3 py-1.5 rounded-lg transition-all ${
                              t.asignado_a === m.user_id
                                ? 'bg-amber-500/30 text-amber-400 border border-amber-500/40'
                                : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
                            }`}>
                            {m.nombre}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ CALENDARIO/EVENTOS TAB ═══ */}
        {tab === 'calendario' && (
          <div className="px-4 pb-24" data-testid="eventos-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">{eventos.length} eventos</p>
              <Button size="sm" onClick={() => { setEventoForm({ titulo: '', fecha: '', tipo: 'casa', notificacion_dias: 1 }); setEditEvento(null); setShowEventoForm(true); }}
                className="bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 h-8 text-xs"
                data-testid="btn-add-evento">
                <Plus className="h-3.5 w-3.5 mr-1" /> Nuevo
              </Button>
            </div>
            <div className="space-y-2">
              {eventos.map(ev => {
                const tc = TIPO_EVENTO[ev.tipo] || TIPO_EVENTO.casa;
                const Icon = tc.icon;
                const daysUntil = Math.ceil((new Date(ev.fecha + 'T00:00:00') - new Date(hoy + 'T00:00:00')) / (1000*60*60*24));
                return (
                  <div key={ev.id} className={`rounded-xl p-3 border ${tc.bg} ${tc.border}`} data-testid={`evento-${ev.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-800/80`}>
                        <Icon className={`h-4 w-4 ${tc.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{ev.titulo}</p>
                        <p className="text-gray-500 text-[10px]">{ev.fecha} &middot; {tc.label}</p>
                      </div>
                      <span className={`text-xs font-bold tabular-nums flex-shrink-0 ${daysUntil <= 3 ? 'text-red-400' : tc.color}`}>
                        {daysUntil === 0 ? 'HOY' : daysUntil < 0 ? 'Pasado' : `${daysUntil}d`}
                      </span>
                      <button onClick={() => { setEventoForm({...ev}); setEditEvento(ev.id); setShowEventoForm(true); }}
                        className="text-gray-600 hover:text-white p-1" data-testid={`btn-edit-ev-${ev.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => deleteEvento(ev.id)}
                        className="text-gray-600 hover:text-red-400 p-1" data-testid={`btn-del-ev-${ev.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {eventos.length === 0 && (
                <div className="text-center py-12 text-gray-600">
                  <CalendarDays className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No hay eventos proximos</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ INVENTARIO TAB ═══ */}
        {tab === 'inventario' && (
          <div className="px-4 pb-24" data-testid="inventario-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-400 text-xs font-bold uppercase tracking-wider">Stock del gato</p>
              <Button size="sm" onClick={() => { setInvForm({ tipo: 'comida', nombre: '', cantidad: 0, unidad: 'kg', umbral: 1 }); setEditInv(null); setShowInvForm(true); }}
                className="bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 h-8 text-xs"
                data-testid="btn-add-inv">
                <Plus className="h-3.5 w-3.5 mr-1" /> Nuevo
              </Button>
            </div>
            <div className="space-y-3">
              {inventario.map(inv => {
                const isLow = inv.cantidad <= inv.umbral;
                return (
                  <div key={inv.id} className={`rounded-xl p-4 border ${isLow ? 'bg-red-900/15 border-red-500/30' : 'bg-gray-900/80 border-gray-800'}`}
                    data-testid={`inv-${inv.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Package className={`h-4 w-4 ${isLow ? 'text-red-400' : 'text-purple-400'}`} />
                        <span className="text-white font-bold text-sm">{inv.nombre}</span>
                        <span className="text-gray-600 text-[10px] uppercase">{inv.tipo}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setInvForm({...inv}); setEditInv(inv.id); setShowInvForm(true); }}
                          className="text-gray-600 hover:text-white p-1"><Edit className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteInv(inv.id)}
                          className="text-gray-600 hover:text-red-400 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    {/* Quantity controls */}
                    <div className="flex items-center gap-3">
                      <button onClick={() => updateInvCantidad(inv.id, Math.max(0, inv.cantidad - 0.5))}
                        className="w-8 h-8 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white text-lg font-bold">-</button>
                      <div className="flex-1 text-center">
                        <span className={`text-3xl font-black tabular-nums ${isLow ? 'text-red-400' : 'text-white'}`}>{inv.cantidad}</span>
                        <span className="text-gray-500 text-sm ml-1">{inv.unidad}</span>
                      </div>
                      <button onClick={() => updateInvCantidad(inv.id, inv.cantidad + 0.5)}
                        className="w-8 h-8 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white text-lg font-bold">+</button>
                    </div>
                    {/* Alert bar */}
                    <div className="mt-2">
                      <div className="w-full bg-gray-800 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full transition-all ${
                          isLow ? 'bg-red-500' : inv.cantidad <= inv.umbral * 2 ? 'bg-amber-500' : 'bg-green-500'
                        }`} style={{ width: `${Math.min(100, (inv.cantidad / (inv.umbral * 3)) * 100)}%` }} />
                      </div>
                      <p className="text-gray-600 text-[9px] mt-1">Umbral alerta: {inv.umbral} {inv.unidad}</p>
                    </div>
                    {isLow && (
                      <div className="mt-2 flex items-center gap-2 bg-red-500/10 rounded-lg px-3 py-1.5 border border-red-500/20">
                        <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
                        <span className="text-red-400 text-xs font-bold">Stock bajo - reponer</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {inventario.length === 0 && (
                <div className="text-center py-12 text-gray-600">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Añade items de inventario del gato</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ DIALOGS ═══ */}

        {/* Gato form */}
        <Dialog open={showGatoForm} onOpenChange={setShowGatoForm}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm">
            <DialogHeader><DialogTitle>Perfil del gato</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Nombre *</label>
                <Input value={gatoForm.nombre || ''} onChange={e => setGatoForm(p => ({...p, nombre: e.target.value}))}
                  className="bg-gray-800 border-gray-700 text-white" data-testid="input-gato-nombre" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Fecha nacimiento</label>
                <Input type="date" value={gatoForm.fecha_nacimiento || ''} onChange={e => setGatoForm(p => ({...p, fecha_nacimiento: e.target.value}))}
                  className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Veterinario</label>
                  <Input value={gatoForm.veterinario_nombre || ''} onChange={e => setGatoForm(p => ({...p, veterinario_nombre: e.target.value}))}
                    placeholder="Nombre" className="bg-gray-800 border-gray-700 text-white text-sm" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Telefono vet.</label>
                  <Input type="tel" value={gatoForm.veterinario_telefono || ''} onChange={e => setGatoForm(p => ({...p, veterinario_telefono: e.target.value}))}
                    placeholder="Telefono" className="bg-gray-800 border-gray-700 text-white text-sm" />
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Microchip</label>
                <Input value={gatoForm.microchip || ''} onChange={e => setGatoForm(p => ({...p, microchip: e.target.value}))}
                  placeholder="Opcional" className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Observaciones</label>
                <textarea value={gatoForm.observaciones || ''} onChange={e => setGatoForm(p => ({...p, observaciones: e.target.value}))}
                  placeholder="Alergias, medicacion, etc."
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm resize-none h-20 focus:outline-none focus:border-amber-500/50" />
              </div>
              <Button onClick={saveGato} className="w-full bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 font-bold"
                data-testid="btn-save-gato">
                Guardar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Tarea form */}
        <Dialog open={showTareaForm} onOpenChange={setShowTareaForm}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm">
            <DialogHeader><DialogTitle>{editTarea ? 'Editar tarea' : 'Nueva tarea'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Tarea *</label>
                <Input value={tareaForm.nombre} onChange={e => setTareaForm(p => ({...p, nombre: e.target.value}))}
                  placeholder="Ej: Limpiar arenero" className="bg-gray-800 border-gray-700 text-white" data-testid="input-tarea-nombre" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Frecuencia</label>
                <select value={tareaForm.frecuencia} onChange={e => setTareaForm(p => ({...p, frecuencia: e.target.value}))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                  {FRECUENCIAS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Asignar a</label>
                <div className="flex gap-2">
                  {miembros.map(m => (
                    <button key={m.user_id} onClick={() => setTareaForm(p => ({...p, asignado_a: m.user_id, asignado_nombre: m.nombre}))}
                      className={`flex-1 text-xs py-2 rounded-lg transition-all ${
                        tareaForm.asignado_a === m.user_id
                          ? 'bg-amber-500/30 text-amber-400 border border-amber-500/40'
                          : 'bg-gray-800 text-gray-400 border border-gray-700 hover:text-white'
                      }`} data-testid={`assign-${m.nombre}`}>
                      {m.nombre}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={saveTarea} disabled={!tareaForm.nombre.trim()}
                className="w-full bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 font-bold"
                data-testid="btn-save-tarea">
                {editTarea ? 'Actualizar' : 'Crear tarea'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Evento form */}
        <Dialog open={showEventoForm} onOpenChange={setShowEventoForm}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm">
            <DialogHeader><DialogTitle>{editEvento ? 'Editar evento' : 'Nuevo evento'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Titulo *</label>
                <Input value={eventoForm.titulo} onChange={e => setEventoForm(p => ({...p, titulo: e.target.value}))}
                  placeholder="Ej: Vacuna anual" className="bg-gray-800 border-gray-700 text-white" data-testid="input-evento-titulo" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Fecha *</label>
                <Input type="date" value={eventoForm.fecha} onChange={e => setEventoForm(p => ({...p, fecha: e.target.value}))}
                  className="bg-gray-800 border-gray-700 text-white" data-testid="input-evento-fecha" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Tipo</label>
                <div className="flex gap-2">
                  {Object.entries(TIPO_EVENTO).map(([key, tc]) => {
                    const Icon = tc.icon;
                    return (
                      <button key={key} onClick={() => setEventoForm(p => ({...p, tipo: key}))}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs transition-all ${
                          eventoForm.tipo === key ? `${tc.bg} ${tc.color} border ${tc.border}` : 'bg-gray-800 text-gray-500 border border-gray-700'
                        }`} data-testid={`tipo-${key}`}>
                        <Icon className="h-3.5 w-3.5" /> {tc.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Avisar con antelacion</label>
                <select value={eventoForm.notificacion_dias} onChange={e => setEventoForm(p => ({...p, notificacion_dias: parseInt(e.target.value)}))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                  <option value={0}>Mismo dia</option>
                  <option value={1}>1 dia antes</option>
                  <option value={2}>2 dias antes</option>
                  <option value={3}>3 dias antes</option>
                  <option value={7}>1 semana antes</option>
                  <option value={14}>2 semanas antes</option>
                </select>
              </div>
              <Button onClick={saveEvento} disabled={!eventoForm.titulo.trim() || !eventoForm.fecha}
                className="w-full bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 font-bold"
                data-testid="btn-save-evento">
                {editEvento ? 'Actualizar' : 'Crear evento'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Inventario form */}
        <Dialog open={showInvForm} onOpenChange={setShowInvForm}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm">
            <DialogHeader><DialogTitle>{editInv ? 'Editar item' : 'Nuevo item'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Nombre *</label>
                <Input value={invForm.nombre} onChange={e => setInvForm(p => ({...p, nombre: e.target.value}))}
                  placeholder="Ej: Pienso Royal Canin" className="bg-gray-800 border-gray-700 text-white" data-testid="input-inv-nombre" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Tipo</label>
                  <select value={invForm.tipo} onChange={e => setInvForm(p => ({...p, tipo: e.target.value}))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                    <option value="comida">Comida</option>
                    <option value="arena">Arena</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Unidad</label>
                  <select value={invForm.unidad} onChange={e => setInvForm(p => ({...p, unidad: e.target.value}))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none">
                    <option value="kg">kg</option>
                    <option value="sacos">sacos</option>
                    <option value="unidades">unidades</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Cantidad actual</label>
                  <Input type="number" step="0.5" min="0" value={invForm.cantidad}
                    onChange={e => setInvForm(p => ({...p, cantidad: parseFloat(e.target.value) || 0}))}
                    className="bg-gray-800 border-gray-700 text-white" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs font-bold uppercase block mb-1">Umbral alerta</label>
                  <Input type="number" step="0.5" min="0" value={invForm.umbral}
                    onChange={e => setInvForm(p => ({...p, umbral: parseFloat(e.target.value) || 0}))}
                    className="bg-gray-800 border-gray-700 text-white" />
                </div>
              </div>
              <Button onClick={saveInv} disabled={!invForm.nombre.trim()}
                className="w-full bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 font-bold"
                data-testid="btn-save-inv">
                {editInv ? 'Actualizar' : 'Crear item'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </>
  );
}
